import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ITooltipWrapperProps } from './types';

// Gap between the trigger and the tooltip (and the minimum viewport margin).
const GAP = 10;
// Below this width the tooltip is not shown (matches the previous max-[900px] hide).
const MOBILE_MAX_WIDTH = 900;

const TooltipWrapper: React.FC<ITooltipWrapperProps> = (props: ITooltipWrapperProps) => {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, flipped: false });

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    const tooltip = tooltipRef.current;
    if (!trigger || !tooltip) {
      return;
    }

    const triggerRect = trigger.getBoundingClientRect();
    const tooltipRect = tooltip.getBoundingClientRect();

    // Place the tooltip to the right of the trigger, flipping to the left when
    // there isn't enough room so it stays inside the viewport.
    const flipped = window.innerWidth - triggerRect.right < tooltipRect.width + GAP;
    const left = flipped ? triggerRect.left - tooltipRect.width - GAP : triggerRect.right + GAP;

    // Vertically centre on the trigger, clamped to the viewport.
    const centred = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
    const top = Math.max(GAP, Math.min(centred, window.innerHeight - tooltipRect.height - GAP));

    setPosition({ top, left, flipped });
  }, []);

  const handleEnter = useCallback(() => {
    if (window.innerWidth <= MOBILE_MAX_WIDTH) {
      return;
    }
    updatePosition();
    setVisible(true);
  }, [updatePosition]);

  const handleLeave = useCallback(() => setVisible(false), []);

  // While visible, keep the tooltip glued to the trigger during scroll/resize.
  useEffect(() => {
    if (!visible) {
      return;
    }
    let frame = 0;
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(updatePosition);
    };
    window.addEventListener('scroll', schedule, true);
    window.addEventListener('resize', schedule);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule, true);
      window.removeEventListener('resize', schedule);
    };
  }, [visible, updatePosition]);

  const tooltipClassName = `Tooltip Tooltip-info${visible ? ' Tooltip-visible' : ''}${position.flipped ? ' Tooltip-flipped' : ''}`;

  return (
    <span ref={triggerRef} className={`TooltipWrapper relative inline-block m-0 p-0${props.className ? ` ${props.className}` : ''}`} onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
      {props.children}
      {typeof document !== 'undefined' &&
        createPortal(
          <div ref={tooltipRef} className={tooltipClassName} style={{ position: 'fixed', top: position.top, left: position.left, margin: 0, maxWidth: 250, zIndex: 1000, pointerEvents: 'none' }}>
            {props.text}
          </div>,
          document.body,
        )}
    </span>
  );
};

export { TooltipWrapper };
